/**
 * Maps Apero appointmentTypeId values to internal ScheduleAppointmentTypes.
 * Returns the internal type or undefined if not found.
 *
 * Example usage:
 *   const type = mapAperoAppointmentTypeId('appt_t_af60b192');
 *   // type === 'CCS_MED_CONSULT' | undefined
 */

import { AppointmentResponse } from "@9amhealth/openapi";
import { isAperoLink, saferUrl } from "./Urls";

const aperoAppointmentTypeIdToInternalType: Record<string, AppointmentResponse.type | undefined> = {
  //region Env: Dev and QA
  appt_t_af60b192: AppointmentResponse.type.CCS_MED_CONSULT,
  appt_t_909d5ab1: AppointmentResponse.type.CCS_MED_CONSULT,
  appt_t_a40ccee4: AppointmentResponse.type.COACHING_1ON1,
  appt_t_451d5d77: AppointmentResponse.type.COACHING_1ON1,
  appt_t_aedcb98d: AppointmentResponse.type.DIETITIAN_VISIT,
  appt_t_7c967752: AppointmentResponse.type.DIETITIAN_VISIT,
  appt_t_d05caf90: AppointmentResponse.type.LAB_RESULTS_REVIEW,
  appt_t_8eadb087: AppointmentResponse.type.ONBOARDING_SPECIALIST,
  appt_t_cdd544de: AppointmentResponse.type.ONBOARDING_SPECIALIST,
  appt_t_ce479dd6: AppointmentResponse.type.ONBOARDING_PROVIDER_VISIT,
  appt_t_584d49e9: AppointmentResponse.type.PHARMACIST_VISIT,
  appt_t_19d82b01: AppointmentResponse.type.SPECIALIST_VISIT,
  appt_t_749c5fa4: AppointmentResponse.type.SYNC_VISIT,
  appt_t_af5f7c92: AppointmentResponse.type.DEMO_USER_VISIT,
  //endregion
  //region Env: Production
  appt_t_e9d71ca9: AppointmentResponse.type.CCS_MED_CONSULT,
  appt_t_5804f282: AppointmentResponse.type.CCS_MED_CONSULT,
  appt_t_6f4a225f: AppointmentResponse.type.COACHING_1ON1,
  appt_t_dd71b151: AppointmentResponse.type.COACHING_1ON1,
  appt_t_a823421e: AppointmentResponse.type.DIETITIAN_VISIT,
  appt_t_703523de: AppointmentResponse.type.DIETITIAN_VISIT,
  appt_t_e0642d0c: AppointmentResponse.type.LAB_RESULTS_REVIEW,
  appt_t_cf2090af: AppointmentResponse.type.ONBOARDING_SPECIALIST,
  appt_t_0ec4de6d: AppointmentResponse.type.ONBOARDING_SPECIALIST,
  appt_t_13c249ea: AppointmentResponse.type.ONBOARDING_PROVIDER_VISIT,
  appt_t_0f51eb19: AppointmentResponse.type.PHARMACIST_VISIT,
  appt_t_9ce9a938: AppointmentResponse.type.SPECIALIST_VISIT,
  appt_t_69b7a57c: AppointmentResponse.type.SYNC_VISIT,
  appt_t_f6e165e4: AppointmentResponse.type.DEMO_USER_VISIT
  //endregion
};

/**
 * Maps an Apero appointmentTypeId to an internal ScheduleAppointmentTypes value.
 * @param appointmentTypeId - The Apero appointmentTypeId string
 * @returns The internal ScheduleAppointmentTypes value, or undefined if not found
 */
export function mapAperoAppointmentTypeId(
  appointmentTypeId: string
): AppointmentResponse.type {
  const type = aperoAppointmentTypeIdToInternalType[appointmentTypeId];
  if (!type) {
    throw new Error(`Unknown appointment typeId: ${appointmentTypeId}`);
  }
  return type;
}

const getAppointmentTypeFromAperoLink = (url: string) => {

  const isApero = isAperoLink(url);
  if (!isApero) return undefined;

  const aperUrl = saferUrl(url);
  const appointmentTypeId = aperUrl.searchParams.get("appointmentTypeId");
  if (!appointmentTypeId) return undefined;

  return mapAperoAppointmentTypeId(appointmentTypeId);
};

export { aperoAppointmentTypeIdToInternalType, getAppointmentTypeFromAperoLink };

